<template>
  <div class="custom-reports-page">
    <div class="custom-reports-layout">
      <aside class="custom-reports-sidebar">
        <div class="custom-reports-sidebar__header">
          <div>
            <h2 class="h1-like reports-info mb-1">
              {{ $t("reports.custom_title", "Custom") }}
            </h2>
            <p class="reports-info mb-0">
              {{ $t("reports.custom_description", "Custom built reports.") }}
            </p>
            <b-form-checkbox
              v-model="showArchived"
              class="custom-reports-show-archived mt-2"
              :disabled="loading"
              @change="onToggleShowArchived"
            >
              {{ $t("reports.custom_show_archived", "Show archived reports") }}
            </b-form-checkbox>
          </div>
          <button class="bttn bttn--sm bttn--orange" @click="fetchReports" :disabled="loading">
            <b-spinner v-if="loading" small class="mr-2" />
            {{ $t("reports.reload_btn", "Load") }}
          </button>
        </div>

        <div v-if="loading" class="custom-reports-sidebar__state">
          <b-spinner class="mb-3" />
          <p class="mb-0">{{ $t("reports.loading_reports", "Loading reports...") }}</p>
        </div>

        <div v-else-if="reports.length === 0" class="custom-reports-sidebar__state">
          <p class="mb-2">{{ $t("reports.custom_empty_title", "No custom reports yet") }}</p>
          <small>
            {{
              $t(
                "reports.custom_empty_description",
                "Published reports for this organization will appear here automatically."
              )
            }}
          </small>
        </div>

        <div v-else class="custom-reports-list-wrap">
          <div class="custom-reports-list">
            <button
              v-for="report in paginatedReports"
              :key="report.id"
              type="button"
              class="custom-reports-list__item"
              :class="{
                'is-active': selectedReport && selectedReport.id === report.id,
                'is-archived': report.archived,
              }"
              @click="selectReport(report)"
            >
              <div class="custom-reports-list__item-top">
                <strong>{{ report.title }}</strong>
                <span v-if="report.archived" class="custom-reports-list__archived-tag">
                  {{ $t("reports.custom_archived_tag", "Archived") }}
                </span>
                <span v-if="report.campaign_id" class="custom-reports-list__badge">
                  {{
                    $t("reports.custom_campaign_badge", "Campaign #{id}", {
                      id: report.campaign_id,
                    })
                  }}
                </span>
              </div>
              <p class="mb-0">
                {{ report.description || $t("reports.custom_no_description", "No description") }}
              </p>
            </button>
          </div>

          <div v-if="totalPages > 1" class="custom-reports-pagination">
            <div class="custom-reports-pagination__summary">
              {{
                $t("reports.custom_pagination_summary", "Showing {from}-{to} of {total}", {
                  from: pageStart,
                  to: pageEnd,
                  total: reports.length,
                })
              }}
            </div>

            <b-pagination
              v-model="currentPage"
              :per-page="pageSize"
              :total-rows="reports.length"
              pills
              size="sm"
              align="center"
              limit="5"
              class="mb-0"
            />
          </div>
        </div>
      </aside>

      <section class="custom-reports-viewer">
        <div v-if="selectedReport" class="custom-reports-viewer__header">
          <div>
            <h3 class="custom-reports-viewer__title">
              {{ selectedReport.title }}
              <span v-if="selectedReport.archived" class="custom-reports-list__archived-tag">
                {{ $t("reports.custom_archived_tag", "Archived") }}
              </span>
            </h3>
            <p class="mb-0 text-muted">{{ selectedReportDescription }}</p>
          </div>
          <div class="custom-reports-viewer__actions">
            <button
              type="button"
              class="bttn bttn--sm bttn--bordered-blue"
              :disabled="archivingId === selectedReport.id"
              @click="toggleArchive(selectedReport)"
            >
              <b-spinner v-if="archivingId === selectedReport.id" small class="mr-2" />
              <template v-if="selectedReport.archived">
                {{ $t("reports.custom_unarchive", "Unarchive") }}
              </template>
              <template v-else>
                {{ $t("reports.custom_archive", "Archive") }}
              </template>
            </button>
            <!-- Reports are private files with no URL that opens on its own, so taking one
                 out of the dashboard is a download rather than a link to a new tab. -->
            <button
              type="button"
              class="bttn bttn--sm bttn--orange"
              :disabled="!reportHtml"
              @click="downloadReport(selectedReport)"
            >
              {{ $t("reports.custom_download", "Download") }}
            </button>
          </div>
        </div>

        <div v-if="selectedReport" class="custom-reports-frame-wrap">
          <div v-if="contentPending" class="custom-reports-frame-wrap__loading">
            <b-spinner class="mb-3" />
            <p class="mb-0">{{ $t("reports.loading_reports", "Loading reports...") }}</p>
          </div>

          <!-- Nothing generated for this report yet (HTTP 409): a state, not a failure. -->
          <div v-else-if="contentMissing" class="custom-reports-viewer__empty h-100">
            <p class="mb-2">
              {{ $t("reports.custom_not_ready_title", "This report has no results yet") }}
            </p>
            <small>
              {{
                $t(
                  "reports.custom_not_ready_description",
                  "It has been created but nothing has been generated for it yet. Build it with AI to produce the first version."
                )
              }}
            </small>
          </div>

          <div v-else-if="contentError" class="custom-reports-viewer__empty h-100">
            <p class="mb-2">
              {{ $t("reports.custom_load_failed_title", "Could not load this report") }}
            </p>
            <small class="mb-3">{{ contentErrorDetail }}</small>
            <button type="button" class="bttn bttn--sm bttn--bordered-blue" @click="loadContent">
              {{ $t("reports.custom_retry", "Try again") }}
            </button>
          </div>

          <!-- The report HTML is written by the report builder and carries script. It is
               rendered from memory via srcdoc, so it would share the dashboard's origin
               (and its session) unless sandboxed: no allow-same-origin, on purpose. Its
               refresh goes through the host bridge, so it needs no token either. -->
          <iframe
            v-else-if="reportHtml"
            ref="reportFrame"
            :key="selectedReport.id"
            class="custom-reports-frame"
            :srcdoc="frameHtml"
            :title="$t('reports.custom_iframe_title', 'Custom report')"
            sandbox="allow-scripts allow-popups allow-forms"
            @load="onFrameLoad"
          />
        </div>

        <div v-else class="custom-reports-viewer__empty">
          <p class="mb-2">{{ $t("reports.custom_select_title", "Select a report") }}</p>
          <small>
            {{
              $t(
                "reports.custom_select_description",
                "Pick a report from the left to render its published HTML here."
              )
            }}
          </small>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import urls, { dashboardHost, publicApiHost } from "@/constants/urls";
import { notificationsMixin } from "@/mixins";
import { createReportFrameBridge } from "@/utils/report-frame-bridge";
import { withReportFrameShim } from "@/utils/report-frame-shim";

export default {
  mixins: [notificationsMixin],

  data() {
    return {
      reports: [],
      selectedReportId: null,
      currentPage: 1,
      pageSize: 6,
      loading: false,
      reportHtml: null,
      contentPending: false,
      contentError: null,
      showArchived: false,
      archivingId: null,
    };
  },

  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.reports.length / this.pageSize));
    },

    paginatedReports() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.reports.slice(start, start + this.pageSize);
    },

    pageStart() {
      if (this.reports.length === 0) {
        return 0;
      }

      return (this.currentPage - 1) * this.pageSize + 1;
    },

    pageEnd() {
      if (this.reports.length === 0) {
        return 0;
      }

      return Math.min(this.currentPage * this.pageSize, this.reports.length);
    },

    selectedReport() {
      return this.reports.find((item) => item.id === this.selectedReportId) || null;
    },

    selectedReportDescription() {
      return (
        this.selectedReport?.description ||
        this.$t("reports.custom_no_description", "No description")
      );
    },

    contentMissing() {
      return this.contentError?.response?.status === 409;
    },

    contentErrorDetail() {
      const fallback = this.$t("reports.custom_load_failed", "Could not load this report.");
      let data = this.contentError?.response?.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          data = null;
        }
      }
      return (data && typeof data === "object" && data.error) || fallback;
    },

    /**
     * The report as the frame renders it: with the frame shim in front of its own
     * scripts, so a report written before the host bridge still refreshes through
     * it. Download hands out the report as stored, without the shim.
     */
    frameHtml() {
      return this.reportHtml ? withReportFrameShim(this.reportHtml, publicApiHost) : "";
    },

    selectedReportQueryId() {
      const queryId = Number(this.$route.query.id);
      return Number.isFinite(queryId) && queryId > 0 ? queryId : null;
    },
  },

  // Only one load is honoured at a time: a slow response for a report the user has
  // already moved away from is dropped.
  latestContentRequestId: 0,

  // The host bridge of the report frame on screen; one per report, so a slow
  // answer for a report the user has moved away from is dropped.
  reportBridge: null,

  watch: {
    selectedReportId() {
      this.resetReportBridge();
      this.loadContent();
    },
  },

  created() {
    this.fetchReports();
  },

  mounted() {
    this.resetReportBridge();
    window.addEventListener("message", this.onFrameMessage);
  },

  beforeDestroy() {
    window.removeEventListener("message", this.onFrameMessage);
    this.$options.reportBridge?.dispose();
    this.$options.reportBridge = null;
  },

  methods: {
    async fetchReports() {
      this.loading = true;

      try {
        const orgId = localStorage.getItem("orgId");
        const url = urls.customReportsList.replace(":orgId", orgId);
        const response = await axios.get(url, {
          params: {
            per_page: 100,
            include_archived: this.showArchived ? 1 : 0,
          },
        });

        this.reports = response.data?.data || [];
        this.normalizeCurrentPage();

        const requestedId = this.selectedReportQueryId;
        const nextReport =
          this.reports.find((item) => item.id === requestedId) ||
          this.reports.find((item) => item.id === this.selectedReportId) ||
          this.reports[0] ||
          null;

        if (nextReport) {
          this.selectReport(nextReport, false);
        } else {
          this.selectedReportId = null;
        }
      } catch (error) {
        this.$_notificationsMixin_handleCatch(error);
      } finally {
        this.loading = false;
      }
    },

    syncSelectedReportQuery(reportId) {
      const reportQueryId = String(reportId);

      if (this.$route.query.id === reportQueryId) {
        return;
      }

      const nextQuery = {
        ...this.$route.query,
        id: reportQueryId,
      };

      this.$router.replace({ query: nextQuery }).catch(() => {});
    },

    syncCurrentPageToReport(reportId) {
      const reportIndex = this.reports.findIndex((item) => item.id === reportId);
      if (reportIndex === -1) {
        return;
      }

      this.currentPage = Math.floor(reportIndex / this.pageSize) + 1;
    },

    normalizeCurrentPage() {
      if (this.reports.length === 0) {
        this.currentPage = 1;
        return;
      }

      this.currentPage = Math.min(this.currentPage, this.totalPages);
    },

    onToggleShowArchived(checked) {
      this.showArchived = checked;
      this.currentPage = 1;
      this.fetchReports();
    },

    async toggleArchive(report) {
      if (!report || this.archivingId) {
        return;
      }

      this.archivingId = report.id;

      try {
        const orgId = localStorage.getItem("orgId");
        const url = urls.customReportArchive
          .replace(":orgId", orgId)
          .replace(":customReportId", report.id);

        await axios.put(url, { archived: !report.archived });

        // Reload so the list reflects the current archived filter (a freshly
        // archived report drops off the default list, etc.).
        await this.fetchReports();
      } catch (error) {
        this.$_notificationsMixin_handleCatch(error);
      } finally {
        this.archivingId = null;
      }
    },

    /**
     * Loads the selected report's HTML. Reports live in private storage and
     * report_url is now the path of an authenticated endpoint, not a URL an iframe
     * or a new tab can open (a navigation carries no Authorization header), so the
     * bytes are fetched with the org JWT and rendered from memory via srcdoc.
     */
    async loadContent() {
      const id = this.selectedReportId;
      const requestId = ++this.$options.latestContentRequestId;

      this.reportHtml = null;
      this.contentError = null;

      if (!id) {
        this.contentPending = false;
        return;
      }

      this.contentPending = true;

      try {
        const orgId = localStorage.getItem("orgId");
        const url = urls.customReportContent
          .replace(":orgId", orgId)
          .replace(":customReportId", id);
        // A whole HTML document, not JSON.
        const response = await axios.get(url, {
          responseType: "text",
          transformResponse: [(d) => d],
        });

        if (requestId !== this.$options.latestContentRequestId) return;
        this.reportHtml = response.data;
      } catch (error) {
        if (requestId !== this.$options.latestContentRequestId) return;
        this.contentError = error;
      } finally {
        if (requestId === this.$options.latestContentRequestId) this.contentPending = false;
      }
    },

    /**
     * Hands the loaded report to the browser as a file. A blob: tab would inherit
     * the dashboard's origin and give report-authored script the reach the sandbox
     * denies, so this is a download, never a new tab.
     */
    downloadReport(report) {
      if (!report || !this.reportHtml) return;

      const blob = new Blob([this.reportHtml], { type: "text/html;charset=utf-8" });
      const objectUrl = URL.createObjectURL(blob);
      const slug =
        String(report.title || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "") || `report-${report.id}`;

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${slug}.html`;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(objectUrl);
    },

    /**
     * Charidy Sign-In host bridge for the report frame (see
     * `utils/report-host-bridge.js`): the report's refresh asks this page to run
     * the request with the dashboard's own session and gets back only the
     * answer. A report of the older `charidy:report:auth` protocol gets its org
     * and hosts with a placeholder token, and its refresh comes in through the
     * frame shim. No token is ever posted into the frame.
     */
    resetReportBridge() {
      this.$options.reportBridge?.dispose();
      this.$options.reportBridge = createReportFrameBridge({
        getFrameWindow: () => this.$refs.reportFrame?.contentWindow || null,
        apiHost: publicApiHost,
        dashboardHost,
        storage: localStorage,
        httpGet: (url, config) => axios.get(url, config),
      });
    },

    onFrameLoad() {
      this.$options.reportBridge?.announceLegacyAuth();
    },

    onFrameMessage(event) {
      this.$options.reportBridge?.handleMessage(event);
    },

    selectReport(report, syncRoute = true) {
      if (!report) {
        return;
      }

      this.syncCurrentPageToReport(report.id);

      if (this.selectedReportId === report.id) {
        if (syncRoute) {
          this.syncSelectedReportQuery(report.id);
        }

        return;
      }

      this.selectedReportId = report.id;

      if (!syncRoute) {
        return;
      }

      this.syncSelectedReportQuery(report.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-reports-page {
  height: 100%;
  min-height: 0;
  display: flex;
}

.custom-reports-layout {
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
  gap: 16px;
  height: 100%;
  min-height: 0;
  flex: 1;
}

.custom-reports-sidebar,
.custom-reports-viewer {
  background: #fff;
  border: 1px solid #e6e9ef;
  border-radius: 16px;
  box-shadow: 0 18px 40px rgba(31, 45, 61, 0.06);
  height: 100%;
  min-height: 0;
}

.custom-reports-sidebar {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.custom-reports-sidebar__header,
.custom-reports-viewer__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px;
  border-bottom: 1px solid #eef1f5;
}

.custom-reports-sidebar__state,
.custom-reports-viewer__empty,
.custom-reports-frame-wrap__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #667085;
  padding: 24px 18px;
}

.custom-reports-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  overflow: auto;
  min-height: 0;
}

.custom-reports-list-wrap {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.custom-reports-pagination {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 12px 12px;
  border-top: 1px solid #eef1f5;
  background: #fff;
}

.custom-reports-pagination__summary {
  padding-top: 12px;
  color: #667085;
  font-size: 12px;
  text-align: center;
}

.custom-reports-list__item {
  width: 100%;
  text-align: left;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.custom-reports-list__item:hover,
.custom-reports-list__item.is-active {
  border-color: #107598;
  box-shadow: 0 14px 28px rgba(16, 117, 152, 0.12);
  transform: translateY(-1px);
}

.custom-reports-list__item-top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 8px;
}

.custom-reports-list__item-top > strong {
  flex: 1 1 auto;
  min-width: 0;
}

.custom-reports-list__badge {
  background: rgba(16, 117, 152, 0.1);
  color: #107598;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.custom-reports-list__item.is-archived {
  opacity: 0.72;
}

.custom-reports-list__archived-tag {
  background: #f1f3f6;
  color: #667085;
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  vertical-align: middle;
}

.custom-reports-show-archived {
  font-size: 13px;
  color: #667085;
}

.custom-reports-viewer__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.custom-reports-viewer {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.custom-reports-viewer__title {
  font-size: 24px;
  line-height: 1.2;
  margin-bottom: 8px;
}

.custom-reports-frame-wrap {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.custom-reports-frame-wrap__loading {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.92);
  z-index: 1;
}

.custom-reports-frame {
  width: 100%;
  height: 100%;
  min-height: 100%;
  border: 0;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
}

@media (max-width: 991px) {
  .custom-reports-layout {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(260px, 36%) minmax(0, 1fr);
  }

  .custom-reports-sidebar__header,
  .custom-reports-viewer__header {
    flex-direction: column;
    align-items: stretch;
  }

  .custom-reports-frame-wrap,
  .custom-reports-frame {
    min-height: 0;
  }
}
</style>
